import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { industries } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const IndustriesMarquee = () => {
  const sectionRef = useRef(null);
  const marqueeRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Glow lines animation
      gsap.fromTo(
        '.glow-line-top, .glow-line-bottom',
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 1.5,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Continuous marquee animation
      const marqueeInner = marqueeRef.current;
      const marqueeWidth = marqueeInner.scrollWidth / 2;

      gsap.to(marqueeInner, {
        x: -marqueeWidth,
        duration: 30,
        ease: 'none',
        repeat: -1,
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Double the industries array for seamless loop
  const doubledIndustries = [...industries, ...industries];

  return (
    <section ref={sectionRef} className="py-12 bg-white relative overflow-hidden">
      {/* Red Glow Background */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-full h-32 bg-gradient-to-r from-transparent via-securelec-red/20 to-transparent blur-3xl" />
      </div>
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-64 h-64 bg-securelec-red/10 rounded-full blur-[100px]" />
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-64 h-64 bg-securelec-red/10 rounded-full blur-[100px]" />

      {/* Top Glow Line */}
      <div className="glow-line-top absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-securelec-red to-transparent origin-center" />

      {/* Section Title */}
      <div className="container mx-auto px-6 mb-6 relative z-20">
        <div className="text-center">
          <span className="inline-block text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
            Industries We Serve
          </span>
        </div>
      </div>

      {/* Marquee Container */}
      <div className="relative">
        <div
          ref={marqueeRef}
          className="flex items-center gap-16 whitespace-nowrap py-6"
        >
          {doubledIndustries.map((industry, index) => (
            <div
              key={`${industry}-${index}`}
              className="flex items-center gap-4 text-securelec-gray/70"
            >
              <span className="text-xl md:text-2xl font-bold tracking-wide uppercase">
                {industry}
              </span>
              <span className="w-2 h-2 bg-securelec-red rounded-full opacity-50" />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Glow Line */}
      <div className="glow-line-bottom absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-securelec-red to-transparent origin-center" />

      {/* Side Fade Gradients */}
      <div className="absolute top-0 left-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10" />
      <div className="absolute top-0 right-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10" />
    </section>
  );
};

export default IndustriesMarquee;
